<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useLayout } from '@/layout/composables/layout';
import TieredMenu from 'primevue/tieredmenu';

const props = defineProps({
    isActive: { type: Boolean, default: false },
    isAnchored: { type: Boolean, default: false }
});

const router = useRouter();
const { layoutState } = useLayout();
const isMobile = ref(false);
const emit = defineEmits(['menu-click']);

const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
});

const userRole = ref(localStorage.getItem('role'));

const allMenuItems = ref([
    {
        label: 'Ma Tournée',
        icon: 'pi pi-chevron-circle-right',
        to: '/departLivreur',
        command: async () => {
            if (isMobile.value) {
                layoutState.sidebarActive.value = false;
                layoutState.staticMenuMobileActive.value = false;
            }
            emit('menu-click');
            await new Promise(resolve => setTimeout(resolve, 50));
            try {
                await router.push('/departLivreur');
            } catch (err) {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Navigation error:', err);
                }
            }
        }
    },
    {
        label: 'Préparation palettes',
        icon: 'pi pi-box',
        to: '/prepaPalettes',
        command: async () => {
            if (isMobile.value) {
                layoutState.sidebarActive.value = false;
                layoutState.staticMenuMobileActive.value = false;
            }
            emit('menu-click');
            await new Promise(resolve => setTimeout(resolve, 50));
            try {
                await router.push('/prepaPalettes');
            } catch (err) {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Navigation error:', err);
                }
            }
        }
    },
    {
        label: 'Mes primes',
        icon: 'pi pi-euro',
        to: '/prime',
        command: async () => {
            if (isMobile.value) {
                layoutState.sidebarActive.value = false;
                layoutState.staticMenuMobileActive.value = false;
            }
            emit('menu-click');
            await new Promise(resolve => setTimeout(resolve, 50));
            try {
                await router.push('/prime');
            } catch (err) {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Navigation error:', err);
                }
            }
        }
    },
    {
        label: 'Mes primes (V2)',
        icon: 'pi pi-sparkles',
        to: '/primes-v2',
        roles: ['Admin'],
        command: async () => {
            if (isMobile.value) {
                layoutState.sidebarActive.value = false;
                layoutState.staticMenuMobileActive.value = false;
            }
            emit('menu-click');
            await new Promise(resolve => setTimeout(resolve, 50));
            try {
                await router.push('/primes-v2');
            } catch (err) {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Navigation error:', err);
                }
            }
        }
    },
    {
        label: 'Ambassadeur',
        icon: 'pi pi-building',
        to: '/ambassadeur',
        command: async () => {
            if (isMobile.value) {
                layoutState.sidebarActive.value = false;
                layoutState.staticMenuMobileActive.value = false;
            }
            emit('menu-click');
            await new Promise(resolve => setTimeout(resolve, 50));
            try {
                await router.push('/ambassadeur');
            } catch (err) {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Navigation error:', err);
                }
            }
        }
    }
]);

const menuItems = computed(() =>
    allMenuItems.value.filter((item) => {
        if (!item.roles || !item.roles.length) return true;
        return item.roles.includes(userRole.value);
    })
);

const transformCategoryToTieredMenu = (item) => {
    return [{
        label: item.label,
        icon: item.icon,
        command: item.command
    }];
};
</script>

<template>
    <div class="tiered-menu-wrapper" :class="{ 'mobile': isMobile }">
        <template v-for="(item, index) in menuItems" :key="index">
            <TieredMenu 
                :model="transformCategoryToTieredMenu(item)"
                :popup="false"
                class="layout-tiered-menu w-full"
                :class="{
                    'layout-menu-slim': !isActive && !isMobile,
                    'layout-menu-mobile': isMobile,
                    'is-active': isActive
                }"
            />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.tiered-menu-wrapper {
    width: 100%;
}

.layout-tiered-menu {
    &.layout-menu-mobile {
        position: fixed;
        top: 0;
        left: -100%;
        height: 100vh;
        width: 100%;
        background: var(--surface-overlay);
        z-index: 1000;
        transition: left 0.3s ease;
        overflow-y: auto;

        &.is-active {
            left: 0;
        }
    }
}

:deep(.p-tieredmenu) {
    border: none;
    background: transparent;

    .p-submenu-list {
        margin: 0;
        padding: 0;
    }
}
</style>